import { Capacitor, registerPlugin } from '@capacitor/core';
import { RecorderKit } from '../cap/recorder';

/**
 * Screen Wake Lock Utility
 * 
 * Prevents the screen from dimming or locking during audio recording.
 * 
 * - On Android: Uses the foreground service (already implemented)
 * - On iOS: Uses RecorderKit native plugin for screen wake lock and recording
 * - On Web: Uses Screen Wake Lock API (modern browsers)
 */

interface RecordingControlPlugin {
  startForegroundService(): Promise<void>;
  stopForegroundService(): Promise<void>;
}

// Standard Capacitor plugin registration
const RecordingControl = registerPlugin<RecordingControlPlugin>('RecordingControl', {
  web: () => ({
    startForegroundService: async () => {},
    stopForegroundService: async () => {}
  })
});

// Wait for plugin to be available in the WebView
async function waitForPlugin(name: string, timeoutMs = 3000) {
  const start = performance.now();
  while (performance.now() - start < timeoutMs) {
    if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(name)) return;
    await new Promise(r => setTimeout(r, 50));
  }
  throw new Error(`${name} not available after ${timeoutMs}ms`);
}

let pluginInitialized = false;

// Ensures bridge is alive and plugin is injected
async function ensurePluginReady() {
  if (!pluginInitialized && Capacitor.isNativePlatform()) {
    await waitForPlugin('RecordingControl');
    pluginInitialized = true;
    console.log('✅ RecordingControl plugin is ready');
  }
}

let wakeLock: WakeLockSentinel | null = null;
let isForegroundServiceRunning = false;

/**
 * Request screen wake lock to keep screen on
 */
export async function requestWakeLock(): Promise<boolean> {
  if (Capacitor.getPlatform() === 'android') {
    // Android uses foreground service to maintain microphone access
    try {
      // Ensure plugin is ready before calling
      await ensurePluginReady();
      
      await RecordingControl.startForegroundService();
      isForegroundServiceRunning = true;
      console.log('✅ Android foreground service started');
      return true;
    } catch (error) {
      console.error('❌ Failed to start foreground service:', error);
      return false;
    }
  }

  if (Capacitor.getPlatform() === 'ios') {
    // iOS uses RecorderKit native plugin for screen wake lock only
    // We don't start recording here since the app uses HealthScribe streaming
    try {
      const { acquired } = await RecorderKit.acquireScreenWakeLock();
      console.log('✅ iOS screen wake lock acquired:', acquired);
      return acquired;
    } catch (error) {
      console.error('❌ Failed to acquire iOS screen wake lock:', error);
      return false;
    }
  }

  // For web, use Screen Wake Lock API if available
  if ('wakeLock' in navigator) {
    try {
      wakeLock = await navigator.wakeLock.request('screen');
      console.log('Screen wake lock acquired');
      
      // Re-acquire wake lock when visibility changes (tab becomes visible again)
      wakeLock.addEventListener('release', () => {
        console.log('Screen wake lock released');
      });
      
      return true;
    } catch (error) {
      console.error('Failed to acquire wake lock:', error);
      return false;
    }
  } else {
    console.warn('Screen Wake Lock API not supported');
    return false;
  }
}

/**
 * Release screen wake lock
 */
export async function releaseWakeLock(): Promise<void> {
  if (Capacitor.getPlatform() === 'android' && isForegroundServiceRunning) {
    // Stop Android foreground service
    try {
      await RecordingControl.stopForegroundService();
      isForegroundServiceRunning = false;
      console.log('✅ Android foreground service stopped');
    } catch (error) {
      console.error('❌ Failed to stop foreground service:', error);
    }
  }

  if (Capacitor.getPlatform() === 'ios') {
    // Release iOS screen wake lock
    try {
      const { released } = await RecorderKit.releaseScreenWakeLock();
      console.log('✅ iOS screen wake lock released:', released);
    } catch (error) {
      console.error('❌ Failed to release iOS screen wake lock:', error);
    }
  }
  
  if (wakeLock !== null) {
    try {
      await wakeLock.release();
      wakeLock = null;
      console.log('Screen wake lock released manually');
    } catch (error) {
      console.error('Failed to release wake lock:', error);
    }
  }
}

/**
 * Check if wake lock is currently active
 */
export async function isWakeLockActive(): Promise<boolean> {
  if (Capacitor.getPlatform() === 'ios') {
    try {
      const { isRecording } = await RecorderKit.isRecording();
      return isRecording;
    } catch (error) {
      console.error('Failed to check iOS recording status:', error);
      return false;
    }
  }
  
  return wakeLock !== null && !wakeLock.released;
}

/**
 * Handle visibility change to re-acquire wake lock if needed
 * Call this in your component's useEffect to handle page visibility changes
 */
export function setupWakeLockVisibilityHandler(): () => void {
  const handleVisibilityChange = async () => {
    if (document.visibilityState === 'visible' && wakeLock !== null && wakeLock.released) {
      console.log('Page became visible, re-acquiring wake lock...');
      await requestWakeLock();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Return cleanup function
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}

/**
 * iOS-specific recording control functions
 * These provide direct access to the RecorderKit plugin for advanced use cases
 */

/**
 * Start recording with a specific filename (iOS only)
 */
export async function startRecording(fileName: string = 'note'): Promise<string | null> {
  if (Capacitor.getPlatform() !== 'ios') {
    console.warn('startRecording is only available on iOS');
    return null;
  }
  
  try {
    const { filePath } = await RecorderKit.startRecording({ fileName });
    console.log('✅ Recording started:', filePath);
    return filePath;
  } catch (error) {
    console.error('❌ Failed to start recording:', error);
    return null;
  }
}

/**
 * Pause recording (iOS only)
 */
export async function pauseRecording(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'ios') {
    console.warn('pauseRecording is only available on iOS');
    return false;
  }
  
  try {
    await RecorderKit.pauseRecording();
    console.log('✅ Recording paused');
    return true;
  } catch (error) {
    console.error('❌ Failed to pause recording:', error);
    return false;
  }
}

/**
 * Resume recording (iOS only)
 */
export async function resumeRecording(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'ios') {
    console.warn('resumeRecording is only available on iOS');
    return false;
  }
  
  try {
    await RecorderKit.resumeRecording();
    console.log('✅ Recording resumed');
    return true;
  } catch (error) {
    console.error('❌ Failed to resume recording:', error);
    return false;
  }
}

/**
 * Stop recording (iOS only)
 */
export async function stopRecording(): Promise<boolean> {
  if (Capacitor.getPlatform() !== 'ios') {
    console.warn('stopRecording is only available on iOS');
    return false;
  }
  
  try {
    await RecorderKit.stopRecording();
    console.log('✅ Recording stopped');
    return true;
  } catch (error) {
    console.error('❌ Failed to stop recording:', error);
    return false;
  }
}

